import { use } from 'react';
import { PassageStateContext } from '@/lib/context/passage-state-provider';

/**
 * Custom hook to access base passage state
 *
 * Provides access to the base passage state (scrollState, passageId, version).
 * Must be used within a PassageStateProvider.
 *
 * For most use cases, use usePassage() instead, which provides derived state like passage lookup.
 * Only use this hook when you need direct access to the base state.
 *
 * @returns PassageContextType with current state and setters
 * @throws Error if used outside of PassageStateProvider
 *
 * @example
 * ```tsx
 * import { usePassageState } from '@/lib/hooks/use-passage-state';
 *
 * function MyComponent() {
 *   const { scrollState: {book, chapter}, setScrollState } = usePassageState();
 *
 *   return (
 *     <div>
 *       <p>Current: {book} {chapter}</p>
 *       <button onClick={() => setScrollState({ book: 'Exodus', chapter: 2, verse: null })}>
 *         Go to Exodus 2
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 */
export function usePassageState() {
    const context = use(PassageStateContext);

    if (!context) {
        throw new Error('usePassageState must be used within a PassageStateProvider');
    }

    return context;
}

